import {
  AnalyticalTable,
  Bar,
  Button,
  DatePicker,
  Dialog,
  Form,
  FormGroup,
  FormItem,
  Input,
  Label,
  TextAlign,
  Title,
  Toast
} from '@ui5/webcomponents-react';
import type { AnalyticalTableInstance } from '@ui5/webcomponents-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useStyles } from './QTProjectImportDialog.jss';
import { FormEvent, useCallback, useRef, useState } from 'react';
import {
  QtProjectImport,
  useGetAllQueryTrackerImportsQuery,
  useGetQtProjectImportQuery
} from 'generated/graphql';
import { useTableCheckBoxes } from 'hooks/useTableCheckBoxes';
import CalendarType from '@ui5/webcomponents-base/dist/types/CalendarType';
import { useQTProjectImportDialog } from 'hooks/useQTProjectImportDialog';
import { useProjectsForImport } from 'hooks/useProjectsForImport';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import ButtonType from '@ui5/webcomponents/dist/types/ButtonType.js';
import TitleLevel from '@ui5/webcomponents/dist/types/TitleLevel.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

type QTProjectImportDialogProps = {
  isDialogOpen: boolean;
  setIsDialogOpen: (isOpen: boolean) => void;
};

export const QTProjectImportDialog = (props: QTProjectImportDialogProps) => {
  const { isDialogOpen, setIsDialogOpen } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const tableInstance = useRef<AnalyticalTableInstance>(null);
  const [selectedProjects, setSelectedProjects] = useState<QtProjectImport[]>([]);

  const {
    isValid,
    endDateValue,
    endDateValueState,
    searchTermValue,
    searchTermValueState,
    setEndDateValue,
    setSearchTermValue,
    setStartDateValue,
    startDateValue,
    startDateValueState,
    validate,
    formatDate
  } = useQTProjectImportDialog();

  const [{ data, fetching }] = useGetQtProjectImportQuery({
    variables: {
      searchTerm: searchTermValue,
      startDate: formatDate(startDateValue),
      endDate: formatDate(endDateValue)
    },
    pause: !isValid,
    requestPolicy: 'cache-and-network'
  });

  const [{ data: qtImportData }, reexecuteQuery] = useGetAllQueryTrackerImportsQuery({
    pause: true
  });

  const refetchImportedQueries = () => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const { handleProjectImport, toastMessage, toastDuration, toastOpen, setToastOpen } =
    useProjectsForImport({
      refetchData: refetchImportedQueries,
      data: qtImportData
    });

  const onSelectedProjects = useCallback(
    (selectedProjects: QtProjectImport[]) => setSelectedProjects(selectedProjects),
    []
  );
  const { renderHeaderCheckbox, renderCheckbox } = useTableCheckBoxes({
    data: data?.projects,
    onSelected: onSelectedProjects,
    tableInstance: tableInstance
  });

  const renderHeader = (level: TitleLevel, content: string, dataHelpId: string) => {
    return (
      <div>
        <Title data-help-id={dataHelpId} data-testid={dataHelpId} level={level} wrappingType="None">
          {content}
        </Title>
      </div>
    );
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validate();
  };

  const onImport = async () => {
    const projectNames = selectedProjects.map((project) => project.projectName);

    handleProjectImport(projectNames, formatDate(startDateValue), formatDate(endDateValue));
    setIsDialogOpen(false);
  };

  return createPortal(
    <>
      <Dialog
        open={isDialogOpen}
        className={classes.dialog}
        footer={
          <Bar
            design="Footer"
            endContent={
              <>
                <Button
                  onClick={onImport}
                  disabled={!selectedProjects.length}
                  design={ButtonDesign.Emphasized}
                  data-testid="import-abap-queries-dialog-button-import"
                >
                  {t('import-abap-queries-dialog-button-import')}
                </Button>
                <Button onClick={() => setIsDialogOpen(false)}>
                  {t('import-abap-queries-dialog-button-close')}
                </Button>
              </>
            }
          />
        }
        headerText={t('import-abap-queries-dialog-header')}
        onClose={() => setIsDialogOpen(false)}
      >
        <Form
          style={{
            alignItems: 'center'
          }}
          onSubmit={onSubmit}
          data-testid="import-abap-queries-dialog-form"
          labelSpan="S12 M2 L2 XL1"
          layout="S1 M1 L1 XL1"
        >
          <FormGroup>
            <FormItem
              labelContent={
                <Label required for="searchTerm" wrappingType="None">
                  {t('import-abap-queries-dialog-form-label-search-term')}
                </Label>
              }
            >
              <Input
                data-testid="import-abap-queries-dialog-form-search-term"
                name="searchTerm"
                required
                type="Text"
                className={classes.formInputItem}
                valueState={searchTermValueState}
                value={searchTermValue}
                onChange={(e) => setSearchTermValue(e.target.value)}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('import-abap-queries-dialog-form-search-term-value-state-message')}
                  </Label>
                }
              />
            </FormItem>
            <FormItem
              labelContent={
                <Label required wrappingType="None">
                  {t('import-abap-queries-dialog-form-label-start-date')}
                </Label>
              }
            >
              <DatePicker
                data-testid="import-abap-queries-dialog-form-start-date"
                primaryCalendarType={CalendarType.Gregorian}
                className={classes.formInputItem}
                name="startDate"
                valueState={startDateValueState}
                value={startDateValue}
                onChange={(e) => setStartDateValue(e.target.value)}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('import-abap-queries-dialog-form-start-date-value-state-message')}
                  </Label>
                }
              />
            </FormItem>
            <FormItem
              labelContent={
                <Label id="form-end-date-label" required wrappingType="None">
                  {t('import-abap-queries-dialog-form-label-end-date')}
                </Label>
              }
            >
              <DatePicker
                aria-labelledby="form-end-date-label"
                data-testid="import-abap-queries-dialog-form-end-date"
                primaryCalendarType={CalendarType.Gregorian}
                className={classes.formInputItem}
                name="endDate"
                valueState={endDateValueState}
                value={endDateValue}
                onChange={(e) => setEndDateValue(e.target.value)}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('import-abap-queries-dialog-form-end-date-value-state-message')}
                  </Label>
                }
              />
              <Button
                className={classes.searchButton}
                type={ButtonType.Submit}
                design={ButtonDesign.Emphasized}
                data-testid="import-abap-queries-dialog-form-button-search"
              >
                {t('import-abap-queries-dialog-form-button-search')}
              </Button>
            </FormItem>
          </FormGroup>
        </Form>
        <AnalyticalTable
          tableInstance={tableInstance}
          columns={[
            {
              Header: () =>
                renderHeaderCheckbox({
                  dataTestId: 'import-abap-queries-dialog-checkbox-all',
                  dataHelpId: 'import-abap-queries-dialog-checkbox-all'
                }),
              hAlign: TextAlign.Center,
              disableSortBy: true,
              id: 'checkbox',
              width: 60,
              Cell: (cellContent: any) => {
                return renderCheckbox(cellContent.row.index, {
                  dataHelpId: 'import-abap-queries-dialog-checkbox',
                  dataTestId: 'import-abap-queries-dialog-checkbox'
                });
              }
            },
            {
              Header: () =>
                renderHeader(
                  TitleLevel.H6,
                  t('import-abap-queries-dialog-table-column-description'),
                  'import-abap-queries-dialog-table-column-description'
                ),
              accessor: 'projectName',
              sortType: 'basic'
            },
            {
              Header: () =>
                renderHeader(
                  TitleLevel.H6,
                  t('import-abap-queries-dialog-table-column-number-queries'),
                  'import-abap-queries-dialog-table-column-number-queries'
                ),
              width: 150,
              accessor: 'numberOfQueries',
              sortType: 'basic'
            }
          ]}
          data={data?.projects || []}
          loading={fetching}
          sortable
        ></AnalyticalTable>
      </Dialog>
      <Toast duration={toastDuration} open={toastOpen} onClose={() => setToastOpen(false)}>
        {toastMessage}
      </Toast>
    </>,
    document.body
  );
};
